import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { DataTable } from '@/components/DataTable';
import {
  Badge,
  Button,
  Card,
  ErrorState,
  Field,
  Input,
  Loading,
  PageHeader,
  Toast,
} from '@/components/ui';
import { formatDateTime } from '@/lib/format';

interface ClientSettings {
  id: string;
  name: string;
  slug: string;
  status: string;
  billing_email: string | null;
  contact_name: string | null;
  contact_phone: string | null;
  timezone: string;
  currency: string;
  max_concurrent_calls: number;
}

interface Integration {
  id: string;
  name: string;
  base_url: string;
  api_user: string | null;
  mock_mode: boolean;
  enabled: boolean;
  last_sync_at: string | null;
  last_error: string | null;
}

interface VicidialTest {
  ok: boolean;
  mock_mode: boolean;
  version: string | null;
  error: string | null;
  integration: string | null;
}

export default function Settings() {
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const [draft, setDraft] = useState<Partial<ClientSettings>>({});
  const [toast, setToast] = useState<{ message: string; tone: 'good' | 'bad' } | null>(null);
  const [testResult, setTestResult] = useState<VicidialTest | null>(null);

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['client-settings'],
    queryFn: () => api.get<ClientSettings>('/settings'),
  });

  useEffect(() => {
    if (data) {
      setDraft({
        contact_name: data.contact_name ?? '',
        contact_phone: data.contact_phone ?? '',
        billing_email: data.billing_email ?? '',
        timezone: data.timezone,
      });
    }
  }, [data]);

  const save = useMutation({
    mutationFn: () => api.patch('/settings', draft),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client-settings'] });
      setToast({ message: 'Settings saved', tone: 'good' });
    },
    onError: (err) =>
      setToast({
        message: errorMessage(err, 'Could not save'),
        tone: 'bad',
      }),
  });

  const test = useMutation({
    mutationFn: () => api.post<VicidialTest>('/vicidial/test'),
    onSuccess: (result) => setTestResult(result),
    onError: (err) =>
      setToast({
        message: errorMessage(err, 'Test failed'),
        tone: 'bad',
      }),
  });

  if (isPending) return <Loading />;
  if (error) return <ErrorState error={error} retry={() => refetch()} />;

  const readOnly = !can('settings:update');

  return (
    <div className="mx-auto max-w-4xl">
      <PageHeader
        title="Settings"
        description={`${data.name} · ${data.slug}`}
        actions={
          !readOnly && (
            <Button loading={save.isPending} onClick={() => save.mutate()}>
              Save changes
            </Button>
          )
        }
      />

      <div className="space-y-6">
        <Card title="Organisation">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Company name">
              <Input value={data.name} disabled />
            </Field>
            <Field label="Status">
              <Input value={data.status} disabled />
            </Field>
            <Field label="Contact name">
              <Input
                value={draft.contact_name ?? ''}
                onChange={(e) => setDraft((d) => ({ ...d, contact_name: e.target.value }))}
                disabled={readOnly}
              />
            </Field>
            <Field label="Contact phone">
              <Input
                value={draft.contact_phone ?? ''}
                onChange={(e) => setDraft((d) => ({ ...d, contact_phone: e.target.value }))}
                disabled={readOnly}
              />
            </Field>
            <Field label="Billing email">
              <Input
                type="email"
                value={draft.billing_email ?? ''}
                onChange={(e) => setDraft((d) => ({ ...d, billing_email: e.target.value }))}
                disabled={readOnly}
              />
            </Field>
            <Field label="Timezone" hint="Used for display only; data is stored in UTC.">
              <Input
                value={draft.timezone ?? ''}
                onChange={(e) => setDraft((d) => ({ ...d, timezone: e.target.value }))}
                disabled={readOnly}
              />
            </Field>
            <Field label="Currency">
              <Input value={data.currency} disabled />
            </Field>
            <Field
              label="Max concurrent calls"
              hint="Set by the platform as part of your plan."
            >
              <Input value={data.max_concurrent_calls} disabled />
            </Field>
          </div>
        </Card>

        <Card
          title="Vicidial connection"
          description="How Glee reaches your dialer."
          actions={
            <Button
              variant="secondary"
              size="sm"
              loading={test.isPending}
              onClick={() => test.mutate()}
            >
              Test connection
            </Button>
          }
          padded={false}
        >
          {testResult && (
            <div
              className={
                testResult.ok
                  ? 'border-b border-line bg-emerald-50 px-4 py-3 text-sm text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300'
                  : 'border-b border-line bg-red-50 px-4 py-3 text-sm text-red-800 dark:bg-red-950/40 dark:text-red-300'
              }
            >
              {testResult.ok ? (
                <>
                  Connected{testResult.version ? ` · Vicidial ${testResult.version}` : ''}
                  {testResult.mock_mode && ' · running in mock mode, no real dialer contacted'}
                </>
              ) : (
                <>Connection failed: {testResult.error ?? 'unknown error'}</>
              )}
            </div>
          )}

          <DataTable<Integration>
            queryKey="vicidial-integrations"
            path="/vicidial-integrations"
            searchable={false}
            emptyTitle="No Vicidial integration configured"
            emptyDescription="Until one exists, the platform runs against the mock dialer."
            columns={[
              {
                key: 'name',
                header: 'Integration',
                render: (integration) => (
                  <div>
                    <div className="font-medium text-ink-900 dark:text-ink-50">
                      {integration.name}
                    </div>
                    <div className="font-mono text-xs text-ink-500">{integration.base_url}</div>
                  </div>
                ),
              },
              {
                key: 'api_user',
                header: 'API user',
                render: (integration) => (
                  <span className="font-mono text-xs">{integration.api_user ?? '—'}</span>
                ),
              },
              {
                key: 'mode',
                header: 'Mode',
                render: (integration) => (
                  <Badge tone={integration.mock_mode ? 'amber' : 'green'}>
                    {integration.mock_mode ? 'Mock' : 'Live'}
                  </Badge>
                ),
              },
              {
                key: 'last_sync',
                header: 'Last sync',
                render: (integration) => (
                  <span className="text-xs text-ink-500">
                    {integration.last_sync_at
                      ? formatDateTime(integration.last_sync_at)
                      : 'Never'}
                  </span>
                ),
              },
              {
                key: 'error',
                header: 'Last error',
                render: (integration) =>
                  integration.last_error ? (
                    <span className="text-xs text-red-600">{integration.last_error}</span>
                  ) : (
                    <span className="text-ink-400">—</span>
                  ),
              },
            ]}
          />
        </Card>

        <Card title="Change your password">
          <ChangePassword onDone={(message, tone) => setToast({ message, tone })} />
        </Card>
      </div>

      {toast && (
        <Toast message={toast.message} tone={toast.tone} onDismiss={() => setToast(null)} />
      )}
    </div>
  );
}

function ChangePassword({
  onDone,
}: {
  onDone: (message: string, tone: 'good' | 'bad') => void;
}) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');

  const change = useMutation({
    mutationFn: () =>
      api.post('/auth/change-password', {
        current_password: current,
        new_password: next,
      }),
    onSuccess: () => {
      setCurrent('');
      setNext('');
      setConfirm('');
      onDone('Password changed. Other sessions have been signed out.', 'good');
    },
    onError: (err) =>
      onDone(errorMessage(err, 'Could not change password'), 'bad'),
  });

  const mismatch = next !== '' && confirm !== '' && next !== confirm;
  const tooShort = next !== '' && next.length < 12;

  return (
    <div className="grid gap-4 sm:grid-cols-3">
      <Field label="Current password">
        <Input
          type="password"
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
          autoComplete="current-password"
        />
      </Field>
      <Field
        label="New password"
        error={tooShort ? 'At least 12 characters' : undefined}
      >
        <Input
          type="password"
          value={next}
          onChange={(e) => setNext(e.target.value)}
          autoComplete="new-password"
        />
      </Field>
      <Field label="Confirm" error={mismatch ? 'Passwords do not match' : undefined}>
        <Input
          type="password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          autoComplete="new-password"
        />
      </Field>
      <div className="sm:col-span-3">
        <Button
          disabled={!current || !next || mismatch || tooShort}
          loading={change.isPending}
          onClick={() => change.mutate()}
        >
          Change password
        </Button>
        <p className="mt-2 text-xs text-ink-500 dark:text-ink-400">
          Changing your password signs you out of every other session.
        </p>
      </div>
    </div>
  );
}
